import type { FloorPlannerLayout, FloorPlannerPoint } from '@repo/frontend-types';
import {
  asArray,
  isLive,
  layoutCurrentDesigns,
  pointInPolygon,
  polygonCentroid,
  usablePoly,
} from '@repo/front-end-utils';

import { decodeRef } from './compositeRefOptions';

/**
 * **Does A contain B?** — the one question about the BUILDING that a configuration statement
 * cannot answer (`complex-measures-in-configuration-composites-containment`, epic ledger rows 8
 * and 51).
 *
 * ## Why this is the wizard's job and not the server's
 *
 * A configuration statement carries observers and totals and **no hierarchy at all**. A zone that
 * physically sits inside an area, with no total linking the two, is invisible from a statement — so
 * `statement-file` cannot refuse it, and Rob ruled on 2026-09-14 that it should not try. The plan
 * is the only place the containment is known at filing time, because for a twin the wizard is about
 * to launch the plan is what mints the entities in the first place.
 *
 * ## Two sources, in a stated order, and only one of them can say "no"
 *
 * Recorded parentage first — which an ADOPTED plan carries richly (38 of 43 surfaces and all 117
 * items on staging plan 51), and which the planner now writes from the drawing for every zone and
 * machine drawn, reshaped or moved (`plan-spatial-parentage`) — and then geometry, for whatever the
 * plan does not record. A recorded parent may also be one the operator CHOSE (`parentChosen`) against
 * the drawing; this walk still only ever CONFIRMS containment with it, and an override of the
 * HIERARCHY does not change which cameras see whom. A parentage MISS is never an
 * answer: "B is genuinely not inside A" and "the plan does not record this parent" are
 * indistinguishable from the chain alone, which is exactly what epic ledger row 51 says.
 *
 * ## `unknown` is a state, never a rounding
 *
 * A question the plan cannot answer returns `'unknown'` and the wizard shows it. Rounding it to
 * `false` is the failure mode that produces a silently wrong total instead of a visible refusal.
 *
 * ## The container's own cameras are the gate, and it is the whole design
 *
 * A space's number comes from one of two places. If it has its own cameras, those cameras see
 * everything standing in it, including whatever is drawn inside it — so counting both double-counts.
 * If instead it is totalled by a composite over named children, its number is exactly those
 * children, the composite edges already describe it fully, and `findDoubleCounts` in
 * `compositeGraph.ts` already decides the case correctly. Applying containment there as well would
 * refuse a correct measure the operator cannot fix. Rob's ruling, 2026-09-19.
 *
 * ⚠ **The gate applies to every container, including the twin and a floor — deliberately.**
 * `observerCountsFromLayout` only ever keys ENTITY references, so neither the twin nor a floor is
 * ever watched on a real plan and neither can contribute a containment refusal. That is right
 * rather than an oversight: the twin's number comes from its composite, never from cameras of its
 * own. The structural rows for both are still implemented and pinned, because this is a pure
 * function whose counts are a parameter.
 *
 * ## A reference is decoded through `decodeRef` and nothing else
 *
 * Nothing here invents an encoding. `encodeRef` / `decodeRef` in `@repo/front-end-utils`
 * (`twin-planner/compositeRefs.ts`, re-exported by `compositeRefOptions.ts`) own the one canonical
 * string form for the twin, a floor and an entity, and this module reads it through that decoder.
 */

/** `true` · `false` · `'unknown'` — and `'unknown'` is never rounded to `false`. */
export type ContainmentAnswer = true | false | 'unknown';

/**
 * Why a pair could not be decided, as a discriminator rather than a sentence.
 *
 * The sentence is composed in `@repo/ui`'s `compositeRules.ts` with `labelOf`, so every part of one
 * message is resolved by one resolver. Building it here would resolve half the sentence through the
 * stage's `labelForRef` and half through the editor's own label map — which holds references the
 * plan has LOST — and the same reference could then read two ways in one sentence.
 */
export type UndecidedReason =
  | 'through-no-outline'
  | 'through-not-on-plan'
  | 'input-no-outline'
  | 'input-no-position'
  | 'input-not-on-plan';

export interface PlanContainment {
  /**
   * Does A contain B? Answers `false` for a container the plan does not watch with cameras of its
   * own (FR5), before any structural question is asked.
   */
  contains(a: string, b: string): ContainmentAnswer;
  /** The reason for an `'unknown'`, or `undefined` for a pair that was decided. */
  whyUnknown(a: string, b: string): UndecidedReason | undefined;
}

/** One live thing on the plan, indexed by `refid`. */
interface PlanNode {
  kind: 'surface' | 'item';
  /** Index into `layoutCurrentDesigns`, which is index-aligned with `layout.floors`. */
  floor: number;
  /** `parentSurfaceBid` for a surface, `parentSpaceBid` for an item — both hold a parent REFID. */
  parentRefid?: string;
  /** Surfaces only. */
  poly?: FloorPlannerPoint[];
  /** Items only. */
  x?: number;
  y?: number;
}

interface Verdict {
  answer: ContainmentAnswer;
  reason?: UndecidedReason;
}

const SEP = String.fromCharCode(31);

const finite = (value: unknown): value is number => typeof value === 'number' && isFinite(value);

// `usablePoly` — the "can this polygon be asked anything?" test — is shared with the plan hierarchy
// and lives in `@repo/front-end-utils` (`twin-planner/geometry.ts`). Why it is checked here rather
// than left to `pointInPolygon` is the whole of FR4: that helper answers a confident `false` for a
// polygon it cannot evaluate.

/**
 * How close a point must be to an edge to count as ON it.
 *
 * A metre is the unit here, so this is sub-nanometre and meaningless as a distance. It exists
 * because a SHARED wall in real plan data is not bit-identical: measured on staging plan 51,
 * `NP2-STN-A005` and `NP2-STN-A009` meet along a wall whose y differs by about 2e-13 between the
 * two polygons. An exact comparison would call those two zones un-shared.
 */
const ON_EDGE_EPSILON = 1e-9;

/** Is `p` on the segment `a→b`, within {@link ON_EDGE_EPSILON}? */
const onSegment = (p: FloorPlannerPoint, a: FloorPlannerPoint, b: FloorPlannerPoint): boolean => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = Math.sqrt(dx * dx + dy * dy);
  if (length === 0) {
    const px = p.x - a.x;
    const py = p.y - a.y;
    return Math.sqrt(px * px + py * py) <= ON_EDGE_EPSILON;
  }
  // Perpendicular distance from the infinite line, then the segment's own extent.
  const area = Math.abs(dx * (p.y - a.y) - dy * (p.x - a.x));
  if (area / length > ON_EDGE_EPSILON) return false;
  const along = ((p.x - a.x) * dx + (p.y - a.y) * dy) / length;
  return along >= -ON_EDGE_EPSILON && along <= length + ON_EDGE_EPSILON;
};

/**
 * Is `p` inside `poly`, **or on its boundary**?
 *
 * ⚠ **The boundary half is the whole point, and leaving it out admits the double counts this rule
 * exists to catch.** `pointInPolygon` is a ray cast, which is boundary-EXCLUSIVE and inconsistent
 * about which edges it excludes: a corner sitting exactly on the container's top edge answers
 * `false`. Since every corner of a zone has to pass, a child drawn FLUSH against its parent's wall
 * — the ordinary way a gym floor plan is drawn — was reported as not contained, and the wizard
 * accepted a total that really does count those people twice. Found by review, 2026-09-19.
 *
 * It is the same convention the edge test below already uses, where a shared edge is deliberately
 * not a crossing; the two halves would otherwise disagree about the same wall.
 */
const pointInOrOnPolygon = (p: FloorPlannerPoint, poly: FloorPlannerPoint[]): boolean => {
  if (pointInPolygon(p, poly)) return true;
  for (let i = 0; i < poly.length; i++) {
    if (onSegment(p, poly[i], poly[(i + 1) % poly.length])) return true;
  }
  return false;
};

/**
 * Does segment `p1→p2` properly cross segment `q1→q2`?
 *
 * **Properly** — strict signs on both orientation tests — so a shared edge, a shared corner or a
 * touch does NOT count. That is the behaviour this rule needs: a zone drawn flush against its
 * parent's wall shares an edge with it and is still inside it.
 */
const properlyCross = (
  p1: FloorPlannerPoint,
  p2: FloorPlannerPoint,
  q1: FloorPlannerPoint,
  q2: FloorPlannerPoint,
): boolean => {
  /*
   * ⚠ **Which side of `a→b` is `c` on — to the SAME tolerance the corner test uses.** A bare sign
   * makes a 2e-13 excursion a crossing, and real plan data has exactly that: two zones sharing a
   * wall differ in the thirteenth decimal. The two halves of this predicate would then disagree
   * about the same wall — the corners calling it shared, the edges calling it crossed — and a
   * flush child would come back not-contained after all, which is the defect this whole pair of
   * checks was rewritten to remove.
   */
  const sideOf = (a: FloorPlannerPoint, b: FloorPlannerPoint, c: FloorPlannerPoint): number => {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const length = Math.sqrt(dx * dx + dy * dy);
    const area = dx * (c.y - a.y) - dy * (c.x - a.x);
    if (length === 0) return 0;
    const distance = area / length;
    if (Math.abs(distance) <= ON_EDGE_EPSILON) return 0;
    return distance > 0 ? 1 : -1;
  };
  const d1 = sideOf(q1, q2, p1);
  const d2 = sideOf(q1, q2, p2);
  const d3 = sideOf(p1, p2, q1);
  const d4 = sideOf(p1, p2, q2);
  return d1 * d2 < 0 && d3 * d4 < 0;
};

/** Does any edge of `inner` cross out through an edge of `outer`? */
const anyEdgeEscapes = (inner: FloorPlannerPoint[], outer: FloorPlannerPoint[]): boolean => {
  for (let i = 0; i < inner.length; i++) {
    const a = inner[i];
    const b = inner[(i + 1) % inner.length];
    for (let j = 0; j < outer.length; j++) {
      if (properlyCross(a, b, outer[j], outer[(j + 1) % outer.length])) return true;
    }
  }
  return false;
};

/**
 * Where B sits, for the geometric test: a single representative point. In practice only an ITEM
 * reaches this — a surface B takes the every-corner test below and returns before `pointOf` is asked
 * — so its surface branch (the vertex mean) is not on any live path. The planner's own parent rule
 * is now `resolvePlanParentage`, which uses an interior point rather than the vertex mean; this
 * module deliberately keeps its own, stricter question.
 *
 * The smallest-area tie-break a parent rule applies is deliberately NOT carried over. That answers
 * "which surface is this thing's parent"; this asks "is this thing inside that one", which needs no
 * tie-break.
 */
const pointOf = (node: PlanNode): FloorPlannerPoint | undefined => {
  if (node.kind === 'item')
    return finite(node.x) && finite(node.y) ? { x: node.x, y: node.y } : undefined;
  return usablePoly(node.poly) ? polygonCentroid(node.poly) : undefined;
};

/** Why B could not be placed, given what kind of thing it is. */
const inputReason = (kind: string): UndecidedReason =>
  kind === 'item' ? 'input-no-position' : 'input-no-outline';

export const buildContainment = (
  layout: FloorPlannerLayout | null | undefined,
  observerCounts: ReadonlyMap<string, number>,
): PlanContainment => {
  /*
   * The CURRENT design of each floor, which is what every other reader in this feature uses —
   * `entityCoverage.ts` for coverage and observer counts, `compositeRefOptions.ts` for the picker.
   * A superseded design's entities are not on the plan and must not answer a question about it.
   */
  const nodes = new Map<string, PlanNode>();
  const floorIndexById = new Map<number, number>();

  const designs = layoutCurrentDesigns(layout ?? null);
  const floors = asArray(layout?.floors);
  floors.forEach((floor, index) => {
    if (typeof floor?.id === 'number') floorIndexById.set(floor.id, index);
  });

  designs.forEach((design, floor) => {
    for (const surface of asArray(design?.surfaces).filter(isLive)) {
      if (typeof surface.refid !== 'string' || !surface.refid) continue;
      nodes.set(surface.refid, {
        kind: 'surface',
        floor,
        parentRefid: surface.parentSurfaceBid || undefined,
        poly: asArray(surface.poly),
      });
    }
    for (const item of asArray(design?.items).filter(isLive)) {
      if (typeof item.refid !== 'string' || !item.refid) continue;
      nodes.set(item.refid, {
        kind: 'item',
        floor,
        parentRefid: item.parentSpaceBid || undefined,
        x: item.x,
        y: item.y,
      });
    }
  });

  /**
   * Every ancestor of `refid`, walking `parentSpaceBid` / `parentSurfaceBid` to the top.
   *
   * ⚠ **The field names say *Bid*; the value is the parent's `refid`.** Every writer agrees:
   * `TwinToPlannerConverter.ts:472,521` write `parentRefid`, `PolygonDrawTool.tsx:120` writes
   * `parent.refid`, `SurfaceShape.tsx:80` writes `surface.refid`, and
   * `ObserverInventoryPanel.tsx:524` reads it back as `=== refid`. So the walk compares refids and
   * needs no BID translation.
   *
   * Iterative and bounded, exactly as `reachableFrom` in `compositeGraph.ts` is: plan parentage is
   * operator-authored and a cycle must terminate the walk rather than the stack.
   */
  const ancestorCache = new Map<string, Set<string>>();
  const ancestorsOf = (refid: string): Set<string> => {
    const cached = ancestorCache.get(refid);
    if (cached) return cached;

    const seen = new Set<string>();
    let cursor = nodes.get(refid)?.parentRefid;
    while (cursor != null && cursor !== '' && !seen.has(cursor)) {
      seen.add(cursor);
      cursor = nodes.get(cursor)?.parentRefid;
    }
    ancestorCache.set(refid, seen);
    return seen;
  };

  const evaluate = (a: string, b: string): Verdict => {
    if (a === b) return { answer: false };

    /*
     * FR5, and it runs FIRST — before the references are even decoded.
     *
     * ⚠ **The order is the correctness, not a tidy-up.** A container the plan does not watch with
     * cameras of its own contributes nothing whatever it is, so this decides the pair completely:
     * `false`, never `'unknown'`. Running it after the decode checks meant an UNENCODABLE
     * reference — the empty string `encodeRef` returns for a null or malformed entry, which its
     * own comment says it exists to produce — came back `'unknown'`, a blocking operator-facing
     * state that says "go and fix the Floor Plan" about a question the plan answers perfectly
     * well. `observerCounts` can never hold a key for such a reference, so the gate settles every
     * one of them. Found by review, 2026-09-19.
     */
    if ((observerCounts.get(a) ?? 0) === 0) return { answer: false };

    const refA = decodeRef(a);
    if (refA === null) return { answer: 'unknown', reason: 'through-not-on-plan' };
    const refB = decodeRef(b);
    if (refB === null) return { answer: 'unknown', reason: 'input-not-on-plan' };

    // Nothing contains the twin.
    if (refB.kind === 'twin') return { answer: false };
    // The twin contains everything else on it.
    if (refA.kind === 'twin') return { answer: true };

    if (refA.kind === 'floor') {
      // A floor contains neither another floor nor itself; the equality case is handled above.
      if (refB.kind === 'floor') return { answer: false };
      const floor = floorIndexById.get(refA.floorId);
      // Borrowing "no outline" here would send the operator to fix an outline a floor never had.
      if (floor === undefined) return { answer: 'unknown', reason: 'through-not-on-plan' };
      const nodeB = nodes.get(refB.refid);
      if (!nodeB) return { answer: 'unknown', reason: 'input-not-on-plan' };
      return { answer: nodeB.floor === floor };
    }

    const nodeA = nodes.get(refA.refid);
    if (!nodeA) return { answer: 'unknown', reason: 'through-not-on-plan' };
    // An item contains nothing — a treadmill has no inside.
    if (nodeA.kind === 'item') return { answer: false };
    // A surface contains no floor.
    if (refB.kind === 'floor') return { answer: false };

    const nodeB = nodes.get(refB.refid);
    if (!nodeB) return { answer: 'unknown', reason: 'input-not-on-plan' };
    if (nodeB.floor !== nodeA.floor) return { answer: false };

    /*
     * FR2 — recorded parentage, and it can only CONFIRM. A chain terminating without reaching A
     * means either "B is genuinely not inside A" or "the plan does not record this parent", and
     * nothing here can tell the two apart, so a miss falls through to the geometry rather than
     * answering `false`.
     */
    if (ancestorsOf(refB.refid).has(refA.refid)) return { answer: true };

    /*
     * FR3 — geometry, the primary path on a plan the wizard drew.
     *
     * ⚠ **Transitivity is free and must not be re-implemented.** A nested polygon's centroid lies
     * inside its container's polygon, so the direct test already answers a two-level nesting. A
     * chained geometric walk would be a second answer to the same question.
     */
    if (!usablePoly(nodeA.poly)) return { answer: 'unknown', reason: 'through-no-outline' };

    /*
     * ⚠ **A surface is inside A when EVERY corner of it is, and a single representative point is
     * not good enough for either direction.**
     *
     * `polygonCentroid` is the mean of the VERTICES, not the area centroid, so it misleads twice.
     * Two CONCENTRIC zones each hold the other's centroid — a 10 × 10 and a 2 × 2 drawn at its
     * middle — so a centroid test reports the big one as sitting inside the small one, and where
     * only the small one has cameras that refuses a measure which is correct. And for a CONCAVE
     * zone, an L or a U, the vertex mean can fall outside the zone itself and even inside a
     * disjoint neighbour that merely shares a wall.
     *
     * Testing every corner answers both: a zone whose corners are all inside A is inside A, and a
     * zone that merely overlaps A is not contained by it — which is the right answer rather than an
     * undecidable one. An earlier attempt returned `'unknown'` when the centroid fell outside its
     * own zone, and the driven-browser run on staging plan 51 showed what that costs: the whole-twin
     * total was blocked with "Reception has no outline on the plan", about a zone whose outline is
     * perfectly good. Found by review and then by re-validating against the real gym, 2026-09-19.
     *
     * An item has no corners, so its single position is the whole of its geometry.
     */
    if (nodeB.kind === 'surface') {
      const corners = nodeB.poly;
      if (!usablePoly(corners)) return { answer: 'unknown', reason: 'input-no-outline' };
      // Hoisted rather than inlined: TypeScript drops a property's narrowing inside a callback.
      const outline = nodeA.poly;

      /*
       * ⚠ **Every corner inside, AND no edge crossing out — both halves are needed for a CONCAVE
       * container.** A gym zone drawn as a U or a C has a notch, and a bar spanning its two arms
       * has all four corners inside the zone while its long edges cross the notch and leave it. The
       * corner test alone answers `true` there and refuses a measure that is correct — which is the
       * same false positive the centroid test produced, one shape further on. Together the two are
       * a sound containment predicate for simple polygons.
       *
       * O(corners × outline), which on the largest real plan is 26 × 26 for one pair and is asked
       * at most a few hundred times per plan change. Found by review, 2026-09-19.
       */
      if (!corners.every(corner => pointInOrOnPolygon(corner, outline))) return { answer: false };
      return { answer: !anyEdgeEscapes(corners, outline) };
    }

    const point = pointOf(nodeB);
    if (!point) return { answer: 'unknown', reason: inputReason(nodeB.kind) };
    // Boundary-inclusive for the same reason a zone's corners are: a machine pushed against the
    // wall of the space watching it is in that space.
    return { answer: pointInOrOnPolygon(point, nodeA.poly) };
  };

  /*
   * Memoised per ORDERED pair, never as a full pairwise product — which on an adopted gym would be
   * 43 surfaces × 160 entities of work nobody asked for (NFR1). The separator is the one
   * `compositeRefOptions` already uses, so a key cannot be forged by a refid containing it.
   */
  const verdicts = new Map<string, Verdict>();
  const verdictFor = (a: string, b: string): Verdict => {
    const key = `${a}${SEP}${SEP}${b}`;
    const cached = verdicts.get(key);
    if (cached) return cached;
    const verdict = evaluate(a, b);
    verdicts.set(key, verdict);
    return verdict;
  };

  return {
    contains: (a, b) => verdictFor(a, b).answer,
    whyUnknown: (a, b) => {
      const verdict = verdictFor(a, b);
      return verdict.answer === 'unknown' ? verdict.reason : undefined;
    },
  };
};
